import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	FormControl,
	Grid,
	MenuItem,
	Select,
	Typography,
	TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import { DARK_MODE, SKIP_PART_REASONS } from '../../constants';
import { connect } from 'react-redux';

// Styled components for the new design
const ModalContainer = styled(Box)({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	backgroundColor: 'rgba(0, 0, 0, 0.5)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	zIndex: 9999,
});

const OuterContainer = styled(Box)(({ themeMode }) => ({
	position: 'relative',
	width: '1326.36px',
	height: '601px',
	background: themeMode === DARK_MODE ? '#151A1F' : '#F2F2F2',
	borderRadius: '48px',
}));

const InnerContainer = styled(Box)(({ themeMode }) => ({
	position: 'absolute',
	width: '1232.73px',
	height: '538.75px',
	left: '46.72px',
	top: '62.25px',
	background:
		themeMode === DARK_MODE
			? 'linear-gradient(115.48deg, #1A2127 31.9%, #151A1F 100%)'
			: 'linear-gradient(115.48deg, #DFDFDF 31.9%, #F1F1F1 99.99%)',
	borderRadius: '24px',
}));

const SideBar = styled(Box)(({ themeMode }) => ({
	position: 'absolute',
	width: '19.51px',
	height: '453.61px',
	background: themeMode === DARK_MODE ? '#262626' : '#ADACA8',
}));

const LeftSideBar = styled(SideBar)({
	left: '0px',
	top: '10.73px',
	borderRadius: '0px 8px 8px 0px',
});

const RightSideBar = styled(SideBar)({
	right: '0px',
	top: '10.73px',
	borderRadius: '8px 0px 0px 8px',
});

const Title = styled(Typography)(({ themeMode }) => ({
	position: 'absolute',
	width: '258px',
	height: '37px',
	left: '99px',
	top: '0px',
	fontFamily:
		themeMode === DARK_MODE
			? "'Uni Neue', sans-serif"
			: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 600,
	fontSize: '30px',
	lineHeight: '37px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
	display: 'flex',
	alignItems: 'center',
}));

const ProgressIndicator = styled(Typography)(({ themeMode }) => ({
	position: 'absolute',
	width: '73px',
	height: '62px',
	left: '105px',
	top: '101px',
	fontFamily:
		themeMode === DARK_MODE
			? "'Uni Neue', sans-serif"
			: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 600,
	fontSize: '50px',
	lineHeight: '62px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
}));

const CloseButton = styled(Button)({
	position: 'absolute',
	right: '20px',
	top: '20px',
	minWidth: 'auto',
	padding: '8px',
	color: '#ACACAC',
	'&:hover': {
		backgroundColor: 'rgba(0, 0, 0, 0.1)',
	},
});

const PartNameLabel = styled(Typography)(({ themeMode }) => ({
	position: 'absolute',
	width: '100px',
	height: '27px',
	left: '288px',
	top: '115px',
	fontFamily:
		themeMode === DARK_MODE
			? "'Uni Neue', sans-serif"
			: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 400,
	fontSize: '20px',
	lineHeight: '27px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
	display: 'flex',
	alignItems: 'center',
}));

const PartNameValue = styled(Typography)(({ themeMode }) => ({
	position: 'absolute',
	width: '101px',
	height: '27px',
	left: '288px',
	top: '160px',
	fontFamily:
		themeMode === DARK_MODE
			? "'Uni Neue', sans-serif"
			: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 700,
	fontSize: '20px',
	lineHeight: '27px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
	display: 'flex',
	alignItems: 'center',
}));

const PartNumberLabel = styled(Typography)(({ themeMode }) => ({
	position: 'absolute',
	width: '112px',
	height: '27px',
	left: '715px',
	top: '115px',
	fontFamily:
		themeMode === DARK_MODE
			? "'Uni Neue', sans-serif"
			: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 400,
	fontSize: '20px',
	lineHeight: '27px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
	display: 'flex',
	alignItems: 'center',
}));

const PartNumberValue = styled(Typography)(({ themeMode }) => ({
	position: 'absolute',
	width: '169px',
	height: '27px',
	left: '715px',
	top: '160px',
	fontFamily:
		themeMode === DARK_MODE
			? "'Uni Neue', sans-serif"
			: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 700,
	fontSize: '20px',
	lineHeight: '27px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
	display: 'flex',
	alignItems: 'center',
}));

const ReasonLabel = styled(Typography)(({ themeMode }) => ({
	position: 'absolute',
	width: '198px',
	height: '27px',
	left: '288px',
	top: '217px',
	fontFamily:
		themeMode === DARK_MODE
			? "'Uni Neue', sans-serif"
			: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 400,
	fontSize: '20px',
	lineHeight: '27px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
	display: 'flex',
	alignItems: 'center',
}));

const ReasonDropdown = styled(Select)(({ themeMode }) => ({
	position: 'absolute',
	width: '404px',
	height: '46px',
	left: '288px',
	top: '256px',
	background: themeMode === DARK_MODE ? '#20272D' : '#F8F8F8',
	border: `1px solid ${themeMode === DARK_MODE ? '#3D4B3D' : '#231F20'}`,
	borderRadius: '8px',
	'& .MuiOutlinedInput-notchedOutline': {
		border: 'none',
	},
	'& .MuiSelect-select': {
		fontFamily:
			themeMode === DARK_MODE
				? "'Uni Neue', sans-serif"
				: "'JLR Emeric', sans-serif",
		fontWeight: 700,
		fontSize: '20px',
		lineHeight: '27px',
		color: themeMode === DARK_MODE ? '#FFFFFF' : '#231F20',
		padding: '10px 18px',
	},
}));

const RemarksLabel = styled(Typography)(({ themeMode }) => ({
	position: 'absolute',
	width: '77px',
	height: '27px',
	left: '716px',
	top: '217px',
	fontFamily:
		themeMode === DARK_MODE
			? "'Uni Neue', sans-serif"
			: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 400,
	fontSize: '20px',
	lineHeight: '27px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
	display: 'flex',
	alignItems: 'center',
}));

const RemarksTextField = styled(TextField)(({ themeMode }) => ({
	position: 'absolute',
	width: '404px',
	height: '111px',
	left: '716px',
	top: '256px',
	'& .MuiOutlinedInput-root': {
		background: themeMode === DARK_MODE ? '#20272D' : '#F8F8F8',
		border: `1px solid ${themeMode === DARK_MODE ? '#3D4B3D' : '#231F20'}`,
		borderRadius: '8px',
		height: '111px',
		'& fieldset': {
			border: 'none',
		},
		'&:hover fieldset': {
			border: 'none',
		},
		'&.Mui-focused fieldset': {
			border: 'none',
		},
	},
	'& .MuiInputBase-input': {
		fontFamily:
			themeMode === DARK_MODE
				? "'Uni Neue', sans-serif"
				: "'JLR Emeric', sans-serif",
		fontWeight: 700,
		fontSize: '20px',
		lineHeight: '27px',
		color: themeMode === DARK_MODE ? '#FFFFFF' : '#231F20',
		padding: '10px 18px',
		'&::placeholder': {
			color: themeMode === DARK_MODE ? '#FFFFFF' : '#231F20',
			opacity: 1,
		},
	},
}));

const ActionButtonContainer = styled(Box)({
	position: 'absolute',
	bottom: '40px',
	left: '50%',
	transform: 'translateX(-50%)',
	display: 'flex',
	gap: '16px',
});

const CancelButton = styled(Button)(({ themeMode }) => ({
	width: '120px',
	height: '40px',
	background: themeMode === DARK_MODE ? '#2A2A2A' : '#FFFFFF',
	border: `1px solid ${themeMode === DARK_MODE ? '#FFFFFF' : '#E0E0E0'}`,
	borderRadius: '8px',
	color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
	fontFamily: "'JLR Emeric', sans-serif",
	fontWeight: 600,
	fontSize: '16px',
	textTransform: 'none',
	'&:hover': {
		background: themeMode === DARK_MODE ? '#3A3A3A' : '#F5F5F5',
	},
}));

const ConfirmButton = styled(Button)(({ themeMode }) => ({
	width: '120px',
	height: '40px',
	background: themeMode === DARK_MODE ? '#FFFFFF' : '#000000',
	borderRadius: '8px',
	color: themeMode === DARK_MODE ? '#000000' : '#FFFFFF',
	fontFamily: "'JLR Emeric', sans-serif",
	fontWeight: 600,
	fontSize: '16px',
	textTransform: 'none',
	'&:hover': {
		background: themeMode === DARK_MODE ? '#E0E0E0' : '#333333',
	},
}));

const colors = {
	bgOuter: DARK_MODE ? '#2B2B2B' : '#F2F2F2',
	bgInner: DARK_MODE
		? 'linear-gradient(115.48deg, #2F2F2F 31.9%, #3A3A3A 99.99%)'
		: 'linear-gradient(115.48deg, #DFDFDF 31.9%, #F1F1F1 99.99%)',
	label: DARK_MODE ? '#E0E0E0' : '#202020',
	value: DARK_MODE ? '#FFFFFF' : '#202020',
	inputBg: DARK_MODE ? '#3A3A3A' : '#F8F8F8',
	border: DARK_MODE ? '#a7a7a7ff' : '#231F20',
	selectColor: DARK_MODE ? '#EAEAEA' : '#231F20',
	buttonGradient:
		// isDark? 'linear-gradient(90deg, #444 0%, #111 100%) :'
		'linear-gradient(90deg, #231F20 0%, #000000 100%)',
	buttonText: '#FFFFFF',
};

const SkipSerialNumber = ({
    open,
    handleClose,
    handleSubmit,
    familyName,
    partNumber,
    currentPartNumber,
    partValidation,
    scanJobResults,
    themeMode,
}) => {
    const isDark = themeMode === DARK_MODE;
    const [reason, setReason] = useState('');
    const [remarks, setRemarks] = useState('');
    const [reasonList, setReasonList] = useState([]);

    const colors = {
        label: isDark ? '#E0E0E0' : '#202020',
        value: isDark ? '#FFFFFF' : '#202020',
        inputBg: isDark ? '#3A3A3A' : '#F8F8F8',
        border: isDark ? '#a7a7a7ff' : '#231F20',
        selectColor: isDark ? '#EAEAEA' : '#231F20',
        buttonGradient: isDark
            ? 'linear-gradient(90deg, #444 0%, #111 100%)'
            : 'linear-gradient(90deg, #231F20 0%, #000000 100%)',
        buttonText: '#FFFFFF',
    };

    useEffect(() => {
        if (scanJobResults && Array.isArray(scanJobResults)) {
            const reasons = scanJobResults
                .map((item) => item.skipReason)
                .filter((r) => r && r.trim() !== '');
            const uniqueReasons = [...new Set(reasons)];
            setReasonList(uniqueReasons);
            if (uniqueReasons.length > 0) setReason(uniqueReasons[0]);
        }
    }, [scanJobResults]);

	return (
		<ModalContainer>
			<OuterContainer themeMode={themeMode}>
				<InnerContainer themeMode={themeMode}>
					<LeftSideBar themeMode={themeMode} />
					<RightSideBar themeMode={themeMode} />

					<Title themeMode={themeMode}>Skip Part Scanning</Title>

					<ProgressIndicator themeMode={themeMode}>
						{currentPartNumber + 1}/{partValidation?.scanJobDetails?.length}
					</ProgressIndicator>

					<CloseButton onClick={handleClose}>
						<CloseIcon />
					</CloseButton>

					<PartNameLabel themeMode={themeMode}>Part Name</PartNameLabel>
					<PartNameValue themeMode={themeMode}>{familyName}</PartNameValue>

					<PartNumberLabel themeMode={themeMode}>Part Number</PartNumberLabel>
					<PartNumberValue themeMode={themeMode}>{partNumber}</PartNumberValue>

					<ReasonLabel themeMode={themeMode}>
						Reason to skip the part
					</ReasonLabel>
					<FormControl>
						<ReasonDropdown
							themeMode={themeMode}
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							IconComponent={(props) => (
								<ExpandMoreIcon 
									sx={{
										position: 'absolute',
										right: '18px',
										top: '50%',
										transform: 'translateY(-50%) rotate(90deg)',
										color: themeMode === DARK_MODE ? '#D9D9D9' : '#231F20',
										pointerEvents: 'none',
									}} 
									{...props} 
								/>
							)}
						>
							{SKIP_PART_REASONS.map((reasonOption, index) => (
								<MenuItem key={index} value={reasonOption}>
									{reasonOption}
												</MenuItem>
											))}
						</ReasonDropdown>
					</FormControl>

					<RemarksLabel themeMode={themeMode}>Remarks</RemarksLabel>
					<RemarksTextField
						themeMode={themeMode}
						multiline
						rows={4}
						placeholder="Please enter"
						value={remarks}
						onChange={(e) => setRemarks(e.target.value.toUpperCase())}
					/>

					<ActionButtonContainer>
						<CancelButton themeMode={themeMode} onClick={handleClose}>
							Cancel
						</CancelButton>
						<ConfirmButton
							themeMode={themeMode}
							onClick={() => handleSubmit(reason)}
						>
							Confirm
						</ConfirmButton>
					</ActionButtonContainer>
				</InnerContainer>
			</OuterContainer>
		</ModalContainer>
	);
};

const mapStateToProps = (state) => ({
	themeMode: state.theme.themeMode,
});

export default connect(mapStateToProps)(SkipSerialNumber);
